import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '@store/hooks';
import {
  hasAnyRole,
  selectAuth,
  selectAuthRoles,
  type AdminRoleCode,
} from '@store/slices/auth';

interface RequireRoleProps {
  allowed: AdminRoleCode[];
}

/**
 * Hard-enforces role access on a route subtree. The sidebar already hides
 * disallowed items, but a user with a bookmarked `/crops` URL — or a
 * business_team member who somehow had their roles change mid-session —
 * needs a deterministic redirect.
 *
 * Renders nothing while auth is still initializing (BootSplash covers the
 * viewport). Once initialized, missing role → redirect to /home with a
 * `denied` location-state flag so /home can optionally surface a toast.
 */
export function RequireRole({ allowed }: RequireRoleProps) {
  const { initializing } = useAppSelector(selectAuth);
  const roles = useAppSelector(selectAuthRoles);
  const location = useLocation();

  if (initializing) return null;

  if (!hasAnyRole(roles, allowed)) {
    return (
      <Navigate
        to="/home"
        replace
        state={{ from: location, denied: true }}
      />
    );
  }

  return <Outlet />;
}
